import React, { useCallback, useMemo } from "react";
import Iconify from "../iconify";
import Image from "next/image";
import IconCode from "@/src/lib/iconCode";
import MapComponent from "./mapView";
import { useRouter } from "next/router";
import Label from "../common/Label";

import Link from "next/link";
import CustomButton from "@/src/lib/ui/Button";
import CustomIconButton from "@/src/lib/ui/iconButton";
import CustomTooltip from "@/src/lib/ui/customTooltip";
import { initiateCall } from "@/src/lib/util/callUtils";

// InfoCard Component memoized
const InfoCard = React.memo(({ bgColor, icon, title, content, onClick }) => (
    <div
        className="flex flex-row items-center w-full h-full rounded-lg"
        style={{
            backgroundColor: bgColor,
            padding: "13px 16px",
        }}
    >
        <CustomIconButton className="mr-2" onClick={onClick}>
            <Iconify icon={icon} color="black" width="30px" />
        </CustomIconButton>
        <div>
            <h6 className="text-black text-base font-semibold md:mb-0.5 mb-0">
                {title}
            </h6>

            <p className="text-black text-sm font-normal">{content}</p>
        </div>
    </div>
));

// Main Component
const CentreInfo = ({ data, centers, selectedCenter, setCurrentSlug }) => {
    const router = useRouter();
    // Memoized event handler
    const handleChipClick = useCallback(
        (urlSlug) => {
            router.push(`/facilities/${urlSlug}`);
        },
        [router]
    );

    const handleFindTest = () => {
        router.push("/find-a-test");
    };

    // Memoized facilities list
    const facilityList = useMemo(
        () =>
            data?.faclityList?.map((facility, index) => (
                <div className="w-auto" key={facility.id || index}>
                    <Link
                        href={`/facilities/${facility.urlSlug}`}
                        title={facility.categoryName}
                        passHref
                    >
                        <Label
                            color="default"
                            // onClick={() => handleChipClick(facility.urlSlug)}
                            className="cursor-pointer"
                        >
                            {facility.categoryName}
                        </Label>
                    </Link>
                </div>
            )),
        [data?.faclityList, handleChipClick]
    );

    // const getDirectionsOnMapHandler = () => {
    //     if (data?.googlePlaceId) {
    //         const url = `https://www.google.com/maps/dir/?api=1&destination=HOD&destination_place_id=${data.googlePlaceId}`;
    //         window.open(url, "_blank"); // Open the link in a new window or tab
    //     }
    // };

    const directionsHref = useMemo(() => {
        if (data?.googlePlaceId) {
            return `https://www.google.com/maps/dir/?api=1&destination=HOD&destination_place_id=${data.googlePlaceId}`;
        }
        if (data?.centreLatitude && data?.centreLongitude) {
            return `https://www.google.com/maps/dir/?api=1&destination=${data.centreLatitude},${data.centreLongitude}`;
        }
        return "#";
    }, [data?.googlePlaceId, data?.centreLatitude, data?.centreLongitude]);
    const directionsTitle = "Click here for directions";

    return (
        <div maxWidth="lg" className="w-full md:pl-8">
            <div className="flex flex-col gap-1">
                {/* Location Information */}
                <div>
                    <h1 className="text-black font-semibold md:text-3xl text-lg">
                        {data?.centreName}
                    </h1>
                    <div className="border-b border-gray-300 border-dashed my-3"></div>
                    <p className="text-black text-sm font-normal pt-">
                        {data?.seoDescription}
                    </p>
                    <div className="md:my-3 my-2">
                        <CustomButton
                            variant="contained"
                            color="primary"
                            className="mt-4 rounded-md"
                            onClick={handleFindTest}
                        >
                            Find Test & Prices
                        </CustomButton>
                    </div>
                </div>

                {/* Map Component */}
                {centers.length > 0 && (
                    <MapComponent
                        centers={centers}
                        selectedCenter={selectedCenter}
                        setCurrentSlug={setCurrentSlug}
                    />
                )}

                <div className="border-b border-gray-300 border-dashed md:my-6 my-2"></div>

                {/* Selected Centre Details */}
                {!!selectedCenter && (
                    <div className="flex flex-col md:gap-4 gap-2">
                        {/* Full-width Card */}
                        <div
                            style={{
                                display: "flex",
                                backgroundColor: "#DFE3E8",
                                padding: "13px 24px",
                                borderRadius: "8px",
                                alignItems: "center",
                                cursor: "pointer",
                            }}
                        >
                            <div className="flex flex-row items-center justify-between w-full">
                                <div className="flex flex-row items-center gap-2">
                                    <Image
                                        src="/images/centre.png"
                                        alt="centre"
                                        width={40}
                                        height={40}
                                    />
                                    <div>
                                        <h6 className="text-black text-lg font-semibold">
                                            {data?.centreLimsName}
                                        </h6>
                                        <p className="text-black text-sm font-normal">
                                            {data?.centreAddress}
                                        </p>
                                    </div>
                                </div>
                                <Link
                                    title={directionsTitle}
                                    href={directionsHref}
                                    passHref
                                    className="cursor-pointer"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Iconify
                                        icon={IconCode.mapMarker}
                                        width={48}
                                        height={48}
                                    />
                                </Link>

                            </div>
                        </div>

                        {/* Facilities Section */}

                        <div className="md:py-4 py-2">
                            <h6 className="text-black font-bold">Facilities</h6>

                            <div className="flex flex-row flex-wrap md:gap-3 gap-2 mt-2">
                                {facilityList}
                            </div>
                        </div>

                        {/* Cards in the second row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 justify-items-center">
                            <div className="w-full">
                                <InfoCard
                                    bgColor="#F4F6F8"
                                    icon={IconCode.phoneCall}
                                    title="Call"
                                    content={data?.centreMobile || ""}
                                    onClick={() =>
                                        initiateCall(data?.centreMobile)
                                    }
                                />
                            </div>
                            <div className="w-full">
                                <InfoCard
                                    bgColor="#F4F6F8"
                                    icon={IconCode.mail}
                                    title="Mail"
                                    content={data?.centreEmail || ""}
                                    onClick={() =>
                                        (window.location.href = `mailto:info@hod.care`)
                                    }
                                />
                            </div>
                            <div className="w-full">
                                <InfoCard
                                    bgColor="#F4F6F8"
                                    icon={IconCode.time}
                                    title="Hours"
                                    content={data?.openingHours || ""}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Virtual Tour */}
                {!!data?.virtualTour && (
                    <div>
                        <p className="bg-slate-200 text-[#1C252E] font-bold py-2 px-2 my-2 rounded">
                            Virtual Tour
                        </p>
                        <iframe
                            src={data?.virtualTour}
                            width="100%"
                            height="400"
                            allowFullScreen
                            loading="lazy"
                        ></iframe>
                    </div>
                )}

                {!!data?.virtualTour && (
                    <div className="border-b border-gray-300 border-dashed my-3"></div>
                )}
            </div>
        </div>
    );
};

export default React.memo(CentreInfo);
